/* @flow */
import * as React from 'react';
import { Redirect } from 'react-router';
import Loadable from 'react-loadable';

import { ESTIMATE_PAGE_PATH } from '../Constants';
import Cities from '../data/Cities';
import {
  CraigslistDeliveryPage,
  DonationsPage,
  JunkRemovalPage,
  ServicesPageChrome,
  SmallMovesPage,
  StorageMovesPage,
  StoreDeliveryPage,
} from './pages/ServicesPage';
import Stores from '../data/Stores';
import { HomePage } from './pages/HomePage';
import Spinner from './components/Spinner';

const createRedirect = (from, to) => {
  return function redirect() {
    return <Redirect path={from} to={to} />;
  };
};

const loadable = loader =>
  Loadable({
    loader,
    delay: 300,
    loading() {
      return <Spinner style={{ margin: 'auto' }} />;
    },
  });

const Routes = [
  {
    component: HomePage,
    path: '/',
    exact: true,
  },
  {
    component: loadable(() => import('./pages/FAQPage/FAQPage')),
    path: '/faq',
  },
  {
    component: loadable(() => import('./pages/SafetyPage/SafetyPage')),
    path: '/safety',
  },
  {
    component: loadable(() => import('./pages/ContactPage/ContactPage')),
    path: '/contact',
  },
  {
    component: loadable(() => import('./pages/PartnersPage/PartnersPage')),
    path: '/partner',
  },
  {
    component: loadable(() => import('./pages/EstimatePage/EstimateIndexPage')),
    path: '/estimate',
  },
  {
    component: loadable(() => import('./pages/BecomeLuggerPage/BecomeLuggerPage')),
    path: '/become-a-lugger',
  },
  {
    component: loadable(() => import('./pages/MarkdownPage/MarkdownPage')),
    path: '/terms',
    markdown: require('../assets/markdown/terms.md'),
    title: 'Terms of Service',
  },
  {
    component: loadable(() => import('./pages/MarkdownPage/MarkdownPage')),
    path: '/privacy',
    markdown: require('../assets/markdown/privacy.md'),
    title: 'Privacy Policy',
  },
  {
    component: createRedirect('/services', '/services/store-delivery'),
    path: '/services',
    exact: true,
  },
  {
    component: ServicesPageChrome,
    path:
      '/services/:id(store-delivery|small-moves|craigslist-delivery|donations|storage-moves|junk-removal)',
    exact: true,
    routes: [
      {
        path: '/services/store-delivery',
        component: StoreDeliveryPage,
      },
      {
        path: '/services/small-moves',
        component: SmallMovesPage,
      },
      {
        path: '/services/craigslist-delivery',
        component: CraigslistDeliveryPage,
      },
      {
        path: '/services/donations',
        component: DonationsPage,
      },
      {
        path: '/services/storage-moves',
        component: StorageMovesPage,
      },
      {
        path: '/services/junk-removal',
        component: JunkRemovalPage,
      },
    ],
  },
  {
    component: loadable(() => import('./pages/BookLuggPage/BookLuggIndexPage')),
    path: '/book',
    exact: true,
  },
  {
    component: loadable(() => import('./pages/BookLuggPage/BookLuggLocationSelectionPage')),
    path: '/book/location',
    exact: true,
    title: 'Pickup & destination',
    step: 2,
  },
  {
    component: loadable(() => import('./pages/BookLuggPage/BookLuggProductSelectionPage')),
    path: '/book/product',
    exact: true,
    title: 'Select a vehicle',
    step: 3,
  },
  {
    component: loadable(() => import('./pages/BookLuggPage/BookLuggFlowPage')),
    path: '/book/(time|item|user|payment|confirm)',
    exact: true,
    routes: [
      {
        path: '/book/time',
        component: loadable(() => import('./pages/BookLuggPage/TimeInformation')),
        title: 'Select a time',
        step: 4,
      },
      {
        path: '/book/item',
        component: loadable(() => import('./pages/BookLuggPage/ItemInformation')),
        title: 'What are you moving?',
        step: 5,
      },
      {
        path: '/book/user',
        component: loadable(() => import('./pages/BookLuggPage/UserInformation')),
        title: 'Personal details',
        step: 6,
      },
      {
        path: '/book/payment',
        component: loadable(() => import('./pages/BookLuggPage/PaymentInformation')),
        title: 'Payment',
        step: 7,
      },
      {
        path: '/book/confirm',
        component: loadable(() => import('./pages/BookLuggPage/ConfirmationStep')),
        title: 'Review',
        step: 8,
      },
    ],
    mobileRoutes: [
      {
        path: '/book/time',
        component: loadable(() => import('./pages/BookLuggPage/TimeInformation')),
        title: 'Set day and time',
        step: 4,
      },
      {
        path: '/book/item',
        component: loadable(() => import('./pages/BookLuggPage/ItemInformation')),
        title: 'What you’re moving',
        step: 5,
      },
      {
        path: '/book/user',
        component: loadable(() => import('./pages/BookLuggPage/UserInformation')),
        title: 'Personal details',
        step: 6,
      },
      {
        path: '/book/payment',
        component: loadable(() => import('./pages/BookLuggPage/PaymentInformation')),
        title: 'Payment',
        step: 7,
      },
      {
        path: '/book/confirm',
        component: loadable(() => import('./pages/BookLuggPage/ConfirmationStep')),
        title: 'Summary',
        step: 8,
      },
    ],
  },
  {
    component: createRedirect('/account', '/account/luggs'),
    path: '/account',
    exact: true,
  },
  {
    component: loadable(() => import('./pages/MyAccountPage/MyAccountPage')),
    path: '/account/(profile|luggs|payment)',
    exact: true,
    routes: [
      {
        component: loadable(() => import('./pages/MyAccountPage/MyLuggsPage')),
        path: '/account/luggs',
        exact: true,
      },
      {
        component: loadable(() => import('./pages/MyAccountPage/MyProfilePage')),
        path: '/account/profile',
        exact: true,
      },
      {
        component: loadable(() => import('./pages/MyAccountPage/PaymentInfoPage')),
        path: '/account/payment',
        exact: true,
      },
    ],
  },
  {
    component: loadable(() => import('./pages/LuggPage/LuggPage')),
    path: '/account/luggs/:tripRequestId([a-z0-9-]{36})',
    exact: true,
  },
  {
    component: loadable(() => import('./pages/LuggReviewPage/LuggReviewPage')),
    path: '/account/luggs/:tripRequestId([a-z0-9-]{36})/review',
    exact: true,
  },
  {
    component: loadable(() => import('./pages/LoginPage/LoginPage')),
    path: '/login',
  },
  {
    exact: true,
    component: loadable(() => import('./pages/CitiesPage/CitiesIndexPage')),
    path: '/cities',
  },
];

//
// Cities
//

Object.keys(Cities).forEach(key => {
  Routes.push({
    path: `/cities/${key}`,
    component: loadable(() => import('./pages/CitiesPage/CityDetailsPage')),
    cityId: key,
  });
});

//
// Stores
//

Object.keys(Stores).forEach(key => {
  Routes.push({
    exact: true,
    path: `/services/${key}-delivery`,
    component: loadable(() => import('./pages/StoreDetailPage/StoreDetailPage')),
    storeId: key,
  });
});

Object.keys(Cities).forEach(city => {
  //
  // City Stores
  // lugg.com/san-francisco/ikea-delivery
  //

  Object.keys(Stores).forEach(store => {
    let path = `/${city}/${store}-delivery`;
    Routes.push({
      path,
      component: loadable(() => import('./pages/StoreDetailPage/StoreDetailPage')),
      storeId: store,
    });
  });

  //
  // City Services
  // lugg.com/san-francisco/small-moves
  //

  Routes.push({
    component: loadable(() => import('./pages/ServicesPage/ServicesPageChrome')),
    path: `/${city}/:id(store-delivery|small-moves|craigslist-delivery|donations|storage-moves|junk-removal)`,
    exact: true,
    routes: [
      {
        path: `/${city}/store-delivery`,
        component: loadable(() => import('./pages/ServicesPage/StoreDeliveryPage')),
      },
      {
        path: `/${city}/small-moves`,
        component: loadable(() => import('./pages/ServicesPage/SmallMovesPage')),
      },
      {
        path: `/${city}/craigslist-delivery`,
        component: loadable(() => import('./pages/ServicesPage/CraigslistDeliveryPage')),
      },
      {
        path: `/${city}/donations`,
        component: loadable(() => import('./pages/ServicesPage/DonationsPage')),
      },
      {
        path: `/${city}/storage-moves`,
        component: loadable(() => import('./pages/ServicesPage/StorageMovesPage')),
      },
      {
        path: `/${city}/junk-removal`,
        component: loadable(() => import('./pages/ServicesPage/JunkRemovalPage')),
      },
    ],
  });
});

//
// 404
//

Routes.push({
  path: '/*',
  component: loadable(() => import('./pages/NotFoundPage/NotFoundPage')),
});

export default Routes;



// WEBPACK FOOTER //
// ./src/ui/routes.js